import { useState, useEffect } from 'react';
import { useDataSource } from '@/contexts/DataSourceContext';
import { apiClient } from '@/services/apiClient';

export interface DashboardSummaryDto {
  employeeId: string;
  currentWeek: {
    start: string;
    end: string;
  };
  thisWeek: {
    totalHours: number;
    targetHours: number;
    status: string;
    deadline: string;
    daysUntilDeadline: number;
    timesheetId?: string;
  };
  actionRequired: {
    items: ActionItemDto[];
  };
  quickSummary: {
    weeklyProgress: {
      logged: number;
      target: number;
    };
    billable: {
      hours: number;
      percentage: number;
    };
    entries: {
      count: number;
      daysWithEntries: number;
    };
    daysUntilDue: number;
    hoursRemaining: number;
  };
  performanceMetrics: {
    billableRevenue: {
      billableHours: number;
      totalHours: number;
      percentage: number;
    };
    thisMonth: {
      submittedCount: number;
    };
    avgWeeklyHoursLast4Weeks: number;
    utilization: {
      rate: number;
      trend: number;
    };
    submissionConsistency: {
      onTimeRateLast12Weeks: number;
      onTimeCount: number;
      lateCount: number;
      missingCount: number;
    };
  };
}

export interface ActionItemDto {
  id: string;
  type: 'rejected' | 'missing-hours' | 'overdue' | 'upcoming-deadline';
  title: string;
  description: string;
  urgency: 'high' | 'medium' | 'low';
  timesheetId?: string;
  date?: string;
  daysRemaining?: number;
  weekEnding?: string;
}

export function useDashboardSummary(employeeId: string, screenId: string = 'dashboard') {
  const { getScreenMode } = useDataSource();
  const [summary, setSummary] = useState<DashboardSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const mode = getScreenMode(screenId);
    
    // Only fetch from API in Live mode
    if (mode !== 'live') {
      setLoading(false);
      return;
    }

    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await apiClient.get<DashboardSummaryDto>(
          `/dashboard/summary?employeeId=${encodeURIComponent(employeeId)}`
        );
        setSummary(data);
      } catch (err) {
        console.error('Error fetching dashboard summary:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [employeeId, screenId, getScreenMode]);

  return { summary, loading, error };
}
